/**
 * Detects a newer deployed build and forces a full page reload.
 *
 * How it works:
 * - Each Vite build writes `/version.json` with a unique build id
 * - The running app polls that file (and checks on tab focus)
 * - If the remote id differs from the id baked into this JS bundle, reload
 *
 * The previous meta-tag-only check could not see new deploys while the SPA
 * stayed open, because it only read the already-loaded document.
 */

declare const __APP_VERSION__: string;

const VERSION_URL = '/version.json';
const STORAGE_KEY = 'app-version';
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

let pollTimer: ReturnType<typeof setInterval> | null = null;
let checking = false;

function runningVersion(): string {
  try {
    return typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '';
  } catch {
    return '';
  }
}

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const response = await fetch(`${VERSION_URL}?_=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { version?: string };
    return typeof data.version === 'string' ? data.version : null;
  } catch {
    return null;
  }
}

function reloadForNewVersion(nextVersion: string): void {
  localStorage.setItem(STORAGE_KEY, nextVersion);
  // Hard navigation so browsers drop the old hashed asset graph.
  window.location.reload();
}

/** Compare running build to what nginx is serving; reload if newer. */
export async function checkAppVersion(): Promise<void> {
  if (checking) return;
  checking = true;
  try {
    const deployed = await fetchDeployedVersion();
    if (!deployed) return;

    const local = runningVersion() || localStorage.getItem(STORAGE_KEY) || '';
    if (local && local !== deployed) {
      reloadForNewVersion(deployed);
      return;
    }

    localStorage.setItem(STORAGE_KEY, deployed);
  } finally {
    checking = false;
  }
}

/** Start periodic + focus checks. Call once from App mount. */
export function startAppVersionWatcher(): () => void {
  void checkAppVersion();

  const onVisible = () => {
    if (document.visibilityState === 'visible') {
      void checkAppVersion();
    }
  };

  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onVisible);

  pollTimer = setInterval(() => {
    void checkAppVersion();
  }, POLL_INTERVAL_MS);

  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', onVisible);
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };
}
